import React, { useEffect, useState } from "react";
import { VideoInstanceType } from "../../types/VideoEmbed";
import { ButtonProps } from "../../types/ctaButton";
import { Script } from "gatsby";
import { Builder } from "@builder.io/react";
import VideoEmbed from "@components/RedesignComponents/VideoEmbed/VideoEmbed";
import { segmentTrack } from "@utils/segment/segment-track";

interface VideoEmbedWrapperProps {
  wistiaId: string;
  backgroundImage: string;
  backgroundImageAltText: string;
  previewEnabled: true;
  ctaButtonProps: ButtonProps;
}

const VideoEmbedWrapper: React.FunctionComponent<VideoEmbedWrapperProps> = ({
  wistiaId,
  backgroundImage,
  backgroundImageAltText,
  previewEnabled = true,
  ctaButtonProps,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [videoInstance, setVideoInstance] = useState<VideoInstanceType | null>(
    null
  );

  useEffect(() => {
    if (videoInstance) {
      setIsPlaying(true);
      videoInstance.play();
    }
  }, [videoInstance]);

  const handleWistiaScript = () => {
    window._wq = window._wq || [];
    window._wq.push({
      id: wistiaId,
      onReady: (video: VideoInstanceType) => {
        setVideoInstance(video);
      },
    });
  };

  const triggerAnalytics = () => {
    segmentTrack("Video Button Clicked", {
      video_name: wistiaId,
      url: window.location.pathname,
      action: "Clicked",
      initiator: "User",
      object: "Video Button",
      source_application: "Marketing",
    });
  };

  return (
    <>
      <Script
        src="https://fast.wistia.com/assets/external/E-v1.js"
        id="wistia-script"
        onLoad={handleWistiaScript}
        async
      />
      <VideoEmbed
        backgroundImage={backgroundImage}
        backgroundImageAltText={backgroundImageAltText}
        wistiaId={wistiaId}
        isPlaying={isPlaying}
        previewEnabled={previewEnabled}
        ctaButtonProps={ctaButtonProps}
        setIsPlaying={setIsPlaying}
        triggerAnalytics={triggerAnalytics}
      />
    </>
  );
};

export default VideoEmbedWrapper;

export const RegisterVideoEmbedWrapper = () => {
  Builder.registerComponent(VideoEmbedWrapper, {
    name: "video-molecule",
    inputs: [
      {
        name: "wistiaId",
        type: "string",
      },
      {
        name: "backgroundImage",
        type: "file",
      },
      {
        name: "backgroundImageAltText",
        type: "longText",
      },
    ],
  });
  Builder.registerComponent(VideoEmbedWrapper, {
    name: "buttonVideo",
    inputs: [
      {
        name: "wistiaId",
        type: "string",
      },
      {
        name: "previewEnabled",
        type: "boolean",
        defaultValue: false,
        hideFromUI: true,
      },
      {
        name: "ctaButtonProps",
        type: "object",
        subFields: [
          {
            name: "copy",
            type: "string",
            defaultValue: "",
            required: true,
          },
          {
            name: "copySize",
            type: "string",
            defaultValue: "bodyRegular",
            enum: ["bodyExtraLarge", "bodyLarge", "bodyRegular"],
            required: true,
          },
          {
            name: "variant",
            type: "string",
            defaultValue: "primarySun",
            enum: [
              "primaryForest",
              "primarySun",
              "secondaryForest",
              "secondarySun",
              "tertiaryForest",
              "tertiarySun",
            ],
            required: true,
          },
        ],
      },
    ],
  });
};

export const RegisterButtonVideo = () => {
  Builder.registerComponent(VideoEmbedWrapper, {
    name: "buttonVideo",
    inputs: [
      {
        name: "wistiaId",
        type: "string",
        required: true,
      },
    ],
  });
};
